import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import GoldParticles from "@/components/GoldParticles";
import { IMAGES } from "@/components/brand";

const VALUES = [
  { suit: "♠", title: "Strategy", copy: "Every challenge rewards the delegate who thinks three moves ahead." },
  { suit: "♦", title: "Intelligence", copy: "Numbers, markets and institutions — mastery is the only currency here." },
  { suit: "♣", title: "Competition", copy: "Fifty institutions, one final table. Composure decides the pot." },
  { suit: "♥", title: "Risk", copy: "Calculated exposure, never chance. Learn to price the unknown." },
  { suit: "♠", title: "Opportunity", copy: "Rooms full of recruiters, founders and the sharpest peers in the country." },
  { suit: "♦", title: "Ambition", copy: "FINATEX delegates do not attend festivals. They build reputations." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden border-b border-white/5 py-24 sm:py-32">
        <GoldParticles density={30} />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow mb-5">The Festival</p>
            <h1 data-testid="about-heading" className="max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-[#F6F4EE]">
              The House Always Wins —<br />
              <span className="text-gold-sheen">Because the House Plays Smarter.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-sm sm:text-base leading-relaxed text-[#A19E95]">
              FINATEX is the flagship intercollegiate festival of the Department of Commerce, CHRIST (Deemed to
              be University), Pune Lavasa. Its casino is a metaphor — for markets, for nerve, for the quiet
              mathematics of winning.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="The Metaphor" title="Why the Casino?" testId="about-metaphor-heading" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-sm sm:text-base leading-relaxed text-[#A19E95]">
                A grand casino is not about luck. It is about probability, psychology, capital allocation and
                reading the room — the exact disciplines of commerce. The felt table is a trading floor. The
                dealer is the market. The chip stack is your balance sheet.
              </p>
              <p className="mt-5 text-sm sm:text-base leading-relaxed text-[#A19E95]">
                FINATEX'26 borrows the aesthetic of old-money gaming rooms — dark wood, champagne gold, ivory
                serif — and fills it with M&amp;A negotiations, forensic audits, live trading and the cultural
                stage. Nothing here is gambled. Everything is earned.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 border border-[#D4AF37]/25" aria-hidden="true" />
              <img src={IMAGES.story} alt="Cards, dice and vintage objects on dark wood" className="relative aspect-[4/3] w-full object-cover" data-testid="about-metaphor-image" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-white/5 bg-[#0B0B0F] py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="The Code of the House"
            title="Six Values Dealt to Every Delegate"
            testId="about-values-heading"
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="about-values-grid">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="group h-full border border-white/8 bg-[#13131A] p-8 transition-colors duration-500 hover:border-[#D4AF37]/40">
                  <span className="text-2xl text-[#D4AF37]/50 transition-colors duration-500 group-hover:text-[#D4AF37]" aria-hidden="true">{v.suit}</span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-[#F6F4EE]">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#A19E95]">{v.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal className="order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -inset-3 border border-[#D4AF37]/25" aria-hidden="true" />
              <img src={IMAGES.speaker} alt="Executive addressing a finance conclave" className="relative aspect-[4/3] w-full object-cover" data-testid="about-institution-image" />
            </div>
          </Reveal>
          <div className="order-1 lg:order-2">
            <SectionHeading eyebrow="The Institution" title="CHRIST (Deemed to be University), Pune Lavasa" testId="about-institution-heading" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-sm sm:text-base leading-relaxed text-[#A19E95]">
                Set in the planned hill-city of Lavasa, the Pune Lavasa campus of CHRIST (Deemed to be
                University) is known for rigorous professional education and a culture of discipline. The
                Department of Commerce carries that standard into FINATEX — a festival built by students,
                judged by industry, and remembered long after the chips are counted.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-6">
                {[
                  ["50+", "Institutions Expected"],
                  ["10", "Official Events"],
                  ["2", "College Levels"],
                  ["2", "Days of Play"],
                ].map(([v, l]) => (
                  <div key={l} className="border-l-2 border-[#D4AF37]/40 pl-4">
                    <p className="font-display text-2xl font-bold text-[#E5C97A]">{v}</p>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#A19E95]">{l}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/register"
                data-testid="about-register-cta"
                className="group mt-10 inline-flex items-center gap-3 bg-[#D4AF37] px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-[#08080A] transition-colors duration-300 hover:bg-[#E5C97A]"
              >
                Join the Table
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
